import React from 'react';
import PropTypes from 'prop-types';

import InfoIcon from '@mui/icons-material/Info';

import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';

import axios from '../axiosClient.js';
import Alert from './common/Alert.jsx';
import PageLoading from './common/PageLoading.jsx';
import YearOverYearHeader from './YearOverYearHeader.jsx';
import YearOverYearRow from './YearOverYearRow.jsx';

const REFRESH_INTERVAL_MS = 300_000;

function YearOverYearTable({ selectedRep, selectedYear, enabled = true }) {
  const [alertError, setAlertError] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [yearOverYearProductGroupComparisons, setYearOverYearProductGroupComparisons] =
    React.useState([]);

  // Fetch explicitly on enable/selection; refresh only after the last request settles.
  React.useEffect(() => {
    const controller = new AbortController();
    let timer;
    setLoading(true);
    setAlertError(null);
    setYearOverYearProductGroupComparisons([]);

    const fetchSummary = async () => {
      try {
        const { data } = await axios.get(
          `/api/year-over-year/${encodeURIComponent(selectedRep)}/${selectedYear}`,
          { signal: controller.signal, timeout: 60000 }
        );
        if (controller.signal.aborted) return;
        setYearOverYearProductGroupComparisons(data);
        setAlertError(null);
      } catch (err) {
        if (controller.signal.aborted) return;
        setAlertError(err.response?.data?.error || err.message);
      } finally {
        if (!controller.signal.aborted) {
          setLoading(false);
          timer = setTimeout(fetchSummary, REFRESH_INTERVAL_MS);
        }
      }
    };

    if (enabled && selectedRep && selectedYear) fetchSummary();
    return () => {
      controller.abort();
      clearTimeout(timer);
    };
  }, [selectedRep, selectedYear, enabled]);

  return (
    <Box>
      <Typography data-cy="year-over-year-title-header" variant="h5" sx={{ textAlign: 'center' }}>
        <Tooltip
          data-cy="year-over-year-title-tooltip"
          title="Shows YTD Net Sales for the selected year in the territory assigned to this sales representative. This is to allow evaluation of territory performance regardless of territory changes."
        >
          <span>
            YTD Net Sales by Territory <InfoIcon sx={{ fontSize: 'small' }} />
          </span>
        </Tooltip>
      </Typography>
      <Alert
        data-cy="monthly-commissions-alert"
        message={alertError}
        setMessage={setAlertError}
        level="error"
      />
      {loading ? (
        <PageLoading />
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <YearOverYearHeader selectedYear={selectedYear} />
            </TableHead>
            <TableBody>
              {yearOverYearProductGroupComparisons.length === 0 && (
                <TableRow>
                  <TableCell colSpan={20} align="center">
                    No category groups configured for this rep and year.
                  </TableCell>
                </TableRow>
              )}
              {yearOverYearProductGroupComparisons.map((item) => (
                <YearOverYearRow key={item.ProductGroup} yearOverYearNetSale={item} />
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
}

YearOverYearTable.propTypes = {
  selectedRep: PropTypes.string.isRequired,
};

export default React.memo(YearOverYearTable);
